import { getQuoteTags } from '@paintscout/util/builder'
import React from 'react'
import type { QuoteTag } from 'paintscout'
import { useIsMobile } from '@paintscout/ui/hooks'
import DropdownSelect from '../DropdownSelect'
import type { DropdownSelectOption, DropdownSelectProps } from '../DropdownSelect'
import { useClientOptions } from '../../providers/ClientOptionsProvider'

export interface TagSelectionProps extends Omit<DropdownSelectProps, 'value' | 'options' | 'onChange' | 'variant'> {
  appliedTags: QuoteTag[]
  handleChange: (tags: QuoteTag[]) => void
}

export default function TagSelection(props: TagSelectionProps) {
  const { appliedTags, onCreateOption } = props
  const { options } = useClientOptions()
  const mobile = useIsMobile()
  const tagOptions = getQuoteTags({ options })

  const mapTagsToOptions = (tags: QuoteTag[]): DropdownSelectOption[] => {
    const mappedTags = [
      ...tags.filter(Boolean).map((tag) => ({
        label: tag.label,
        value: tag.name,
        dotColor: tag.color
      }))
    ]
    return mappedTags.filter((tag, index) => mappedTags.findIndex((t) => t.value === tag.value) === index)
  }

  const handleChange = (options: DropdownSelectOption[]) => {
    const tags = options.map((option) => {
      const existingOption = tagOptions.find((tag) => tag.name === option.value)
      if (existingOption) {
        return existingOption
      }
      return {
        name: option.value,
        label: option.label,
        color: option.dotColor
      }
    }) as QuoteTag[]

    props.handleChange(tags)
  }

  return (
    <DropdownSelect
      value={mapTagsToOptions(appliedTags)}
      options={mapTagsToOptions(tagOptions)}
      placeholder={{
        plural: 'Tags'
      }}
      variant="multi"
      searchable
      onChange={handleChange}
      onCreateOption={onCreateOption}
      popperPlacement={mobile ? 'top-start' : 'bottom-start'}
      popperModifiers={{
        flip: {
          enabled: false
        },
        preventOverflow: {
          enabled: false
        }
      }}
      {...props}
    />
  )
}
